import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

export function OrbitVisualizer() {
  const [orbitRadius, setOrbitRadius] = useState<number>(150);
  const [starMass, setStarMass] = useState<number>(5);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const angleRef = useRef<number>(0);
  const lastDistanceRef = useRef<number>(150);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleDistanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setOrbitRadius(val);

    const prev = lastDistanceRef.current;
    if (val >= 2.0 * prev) {
      setShowTooltip(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
      }, 2000);
      lastDistanceRef.current = val;
    } else if (val <= 0.5 * prev) {
      lastDistanceRef.current = val;
    } else if (val < prev && Math.abs(val - prev) > 40) {
      lastDistanceRef.current = val;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2;

    // Clear background
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // Physics: Accumulate angle based on Keplerian angular speed
    const orbitalSpeed = Math.sqrt(starMass / orbitRadius) * 60;
    angleRef.current += (orbitalSpeed * deltaTime);

    // Planet position
    const px = cx + Math.cos(angleRef.current) * orbitRadius;
    const py = cy + Math.sin(angleRef.current) * orbitRadius;

    // Draw dashed Orbit Path
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 6]);
    ctx.beginPath();
    ctx.arc(cx, cy, orbitRadius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]); // Reset dashed state

    // Draw Star (Central Body)
    const starGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 28);
    starGlow.addColorStop(0, '#FCD34D'); // Yellow
    starGlow.addColorStop(0.3, '#F59E0B'); // Orange orange
    starGlow.addColorStop(1, 'rgba(245, 158, 11, 0)');
    ctx.fillStyle = starGlow;
    ctx.beginPath();
    ctx.arc(cx, cy, 28, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#FBBF24';
    ctx.beginPath();
    ctx.arc(cx, cy, 12, 0, Math.PI * 2);
    ctx.fill();

    // Draw Planet
    ctx.fillStyle = '#60A5FA'; // Blue planet
    ctx.beginPath();
    ctx.arc(px, py, 10, 0, Math.PI * 2);
    ctx.fill();

    // Calculate Gravitational Force
    const rawForce = starMass / (orbitRadius * orbitRadius);
    const labelForce = rawForce * 10000;
    const arrowLength = Math.min(labelForce * 10, 120);

    // Angle of pull (from planet to star)
    const pullAngle = Math.atan2(cy - py, cx - px);

    // Parallel offset calculations to draw two force arrows
    const perpX = -Math.sin(pullAngle) * 5;
    const perpY = Math.cos(pullAngle) * 5;

    const drawForceArrow = (startX: number, startY: number) => {
      const endX = startX + Math.cos(pullAngle) * arrowLength;
      const endY = startY + Math.sin(pullAngle) * arrowLength;

      // Arrow shaft
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = 'rgba(239, 68, 68, 0.85)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Arrowhead pointing towards central star
      ctx.beginPath();
      ctx.moveTo(endX, endY);
      ctx.lineTo(
        endX - 7 * Math.cos(pullAngle - Math.PI / 6),
        endY - 7 * Math.sin(pullAngle - Math.PI / 6)
      );
      ctx.lineTo(
        endX - 7 * Math.cos(pullAngle + Math.PI / 6),
        endY - 7 * Math.sin(pullAngle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fillStyle = 'rgba(239, 68, 68, 0.85)';
      ctx.fill();
    };

    if (arrowLength > 5) {
      drawForceArrow(px + perpX, py + perpY);
      drawForceArrow(px - perpX, py - perpY);
    }

    // Force Value Label next to arrowhead (above the vector)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    
    // Position label slightly offset along the vector
    const labelX = px + Math.cos(pullAngle) * (arrowLength + 18);
    const labelY = py + Math.sin(pullAngle) * (arrowLength + 18);
    ctx.fillText(`F = ${labelForce.toFixed(1)}`, labelX, labelY);

    // Draw Simulation Info Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Gravity & Orbital Mechanics Visualizer', 12, 24);
  }, [orbitRadius, starMass]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10">
      <canvas ref={canvasRef} className="block w-full h-full" />
      
      {/* HTML Absolute Tooltip overlay */}
      {showTooltip && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-amber-500 text-slate-950 font-bold px-3 py-1.5 rounded-lg text-[10px] tracking-wide uppercase border border-amber-300 shadow-xl transition-all duration-300 animate-pulse pointer-events-none">
          Force &divide;4 (inverse square law!)
        </div>
      )}

      {/* HTML absolute overlay for sliders */}
      <div className="absolute bottom-3 left-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-2 min-w-[240px]">
        <div className="flex flex-col gap-1">
          <span className="text-white text-[10px] uppercase font-bold tracking-wider opacity-60">
            Orbital Distance
          </span>
          <input
            type="range"
            min="80"
            max="220"
            step="1"
            value={orbitRadius}
            onChange={handleDistanceChange}
            className="w-full accent-cyan-400 cursor-pointer h-1 bg-white/20 rounded-lg appearance-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-white text-[10px] uppercase font-bold tracking-wider opacity-60">
            Star Mass
          </span>
          <input
            type="range"
            min="1"
            max="10"
            step="0.5"
            value={starMass}
            onChange={(e) => setStarMass(parseFloat(e.target.value))}
            className="w-full accent-cyan-400 cursor-pointer h-1 bg-white/20 rounded-lg appearance-none"
          />
        </div>
      </div>
    </div>
  );
}
